/* ============================================================
   Local-day helpers. Tasks belong to a calendar day, stored as
   "YYYY-MM-DD" strings — date-only, built from local getters so
   they never drift across timezones or DST. String order equals
   day order, so comparisons are plain `<` / `===`.
   ============================================================ */

/** Local "YYYY-MM-DD" for a Date (uses local getters, not toISOString/UTC). */
export function toDayStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Local "YYYY-MM-DD" for today. */
export function todayStr(): string {
  return toDayStr(new Date());
}

/** The three numbers in a "YYYY-MM-DD" string. A malformed string yields NaN
   parts (and an Invalid Date downstream) rather than a throw. */
function ymd(str: string): [number, number, number] {
  const [y = NaN, m = NaN, d = NaN] = str.split('-').map(Number);
  return [y, m, d];
}

/** `n` days after a day string (n may be negative), as a day string. */
export function addDaysStr(str: string, n: number): string {
  const [y, m, d] = ymd(str);
  const date = new Date(y, m - 1, d + n);
  return toDayStr(date);
}

/** The upcoming Saturday (today if it's already Saturday), as a day string. */
export function nextWeekendStr(): string {
  const d = new Date();
  const delta = (6 - d.getDay() + 7) % 7; // 6 = Saturday
  return addDaysStr(todayStr(), delta);
}

/** Which weekday a week begins on — a `Date.getDay()` index (0 = Sunday …
   6 = Saturday). Lives here so the date helpers don't depend on the React
   layer; PrefsContext re-exports it for consumers. */
export type WeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Full weekday names indexed by `getDay()` (0 = Sunday) — for the settings
   picker and its current-value label. */
export const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** Single-letter headers indexed by `getDay()` (0 = Sunday). */
const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Midnight copy of a date, so day math ignores the clock time. */
function midnight(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

/** The date (at midnight) that begins the week containing `d`, honoring the
   user's week-start preference (any weekday). */
export function weekStartOf(d: Date, weekStartsOn: WeekStart): Date {
  const m = midnight(d);
  // days to step back from `d`'s weekday to reach the chosen start day
  const back = (m.getDay() - weekStartsOn + 7) % 7;
  m.setDate(m.getDate() - back);
  return m;
}

/** Single-letter weekday headers rotated to begin at `weekStartsOn`. */
export function weekdayLabels(weekStartsOn: WeekStart): string[] {
  return WEEKDAY_INITIALS.map((_, i) => WEEKDAY_INITIALS[(weekStartsOn + i) % 7] ?? '');
}

/** The day an overdue task came from, as a short stamp for its row — phrased
   the way Todoist / Things date-stamp a late item: "Yesterday" for the day
   just past, the weekday while it's still inside the last week ("Monday"),
   then a plain date ("Aug 12", with the year once it isn't this one). Stays
   factual — the day it was for, not how late it is. */
export function overdueLabel(day: string): string {
  const today = todayStr();
  if (day === addDaysStr(today, -1)) return 'Yesterday';
  const [y, m, d] = ymd(day);
  const date = new Date(y, m - 1, d);
  // inside the last week the weekday name is the quickest read; at exactly a
  // week back it repeats today's weekday, so fall through to the date
  if (day > addDaysStr(today, -7)) return date.toLocaleDateString(undefined, { weekday: 'long' });
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (y !== new Date().getFullYear()) opts.year = 'numeric';
  return date.toLocaleDateString(undefined, opts);
}

/** Minutes since local midnight (0–1439) → a clock label for display: "3:05 PM",
   or "15:05" for a locale on a 24-hour clock. Locale-aware, like every other
   label in this file.

   The backend renders its own (always-12-hour) label into reminder push copy,
   since a Lambda has no view of the device's locale — see `formatMinutes` in
   backend/src/services/task-reminder-dispatch.ts. */
export function formatTime(minutes: number): string {
  const d = new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Does this device's locale write clock times on a 12-hour cycle?

   Asked by testing whether `formatTime`'s own formatter emits an AM/PM part,
   rather than by reading `hour12` — that keeps the picker's shape (12 hours plus
   AM/PM, or a bare 0–23) tied to the label the user actually reads, including
   the locales whose hour cycle is h11/h23/h24 rather than a plain boolean. */
export function usesHour12(): boolean {
  try {
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
      .formatToParts(new Date(2000, 0, 1, 13, 0))
      .some((p) => p.type === 'dayPeriod');
  } catch {
    // No `formatToParts` on this engine's Intl: fall back to the 12-hour shape
    // the picker hardcoded before this existed, rather than taking the screen
    // down over a cosmetic question.
    return true;
  }
}

/** A date-section header for the Plan agenda — date first, then the relative
   word: "Aug 9 · Today", "Aug 10 · Tomorrow", "Aug 11 · Friday". */
export function dayLabel(day: string): string {
  const today = todayStr();
  const [y, m, d] = ymd(day);
  const date = new Date(y, m - 1, d);
  const md = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  let rel: string;
  if (day === today) rel = 'Today';
  else if (day === addDaysStr(today, 1)) rel = 'Tomorrow';
  else rel = date.toLocaleDateString(undefined, { weekday: 'long' });
  return `${md} · ${rel}`;
}
